/**
 * useSaveEntityReference
 *
 * Cross-store writer that records an entity-record selection on the surrounding
 * form version: persists the reference under `entityReferences` and refreshes the
 * de-duplicated `allEntityIdsAndGroupIds` index. It never writes `fields` —
 * populating form fields from a reference is the server-owned Form Derive engine's job.
 *
 * The selected entity comes from `useEntityRecordScope()` (the scope's
 * `entityId`, the key the scope was opened with — not the loaded entity doc).
 * The host form version is read non-reactively inside the callback via
 * `snapshot(formStore.state)` and written through `getFormVersionPersist().setMerge`,
 * so this hook never subscribes its callers to form-version writes.
 *
 * When `triggerSearchSync` is set the write is flushed immediately and awaited
 * before the search reindex fires, so `updateSearchIndexOnDemand` reads the
 * post-write document rather than racing the debounce. Otherwise the write is
 * debounced as usual.
 *
 * Every branch — selection, clear and inline rename — stamps document-level
 * `meta.lastModified` as well as the reference's own. The client persist layer
 * bumps only `modifiedAt`, and a form version saved without that stamp is a
 * retained version point-in-time recovery can neither name nor reach (SYD-2450).
 */
import { useCallback, useContext } from 'react';
import { snapshot } from 'valtio';
import { serverTimestamp, deleteField } from 'firebase/firestore';
import { AgentType, type EntityRecord, type FormType, type FormVersion, type TimeType } from '#types/System.types';
import { useEntityRecordScope, formStore } from '../store';
import { UserContext } from '../providers/UserContext';
import { MessageContext } from '../providers/MessageContext';
import { syncDocumentToSearch } from '../services/searchSyncService';
import { entityReferenceIndex } from '#types';
import { entityIdByReferenceKey } from './entityIdByReferenceKey';

export type SaveEntityReference = (
    value: string | undefined,
    entityGroupId?: string,
    entityRecordData?: EntityRecord,
    /** When true, reindex the host form version in search after the write (mirrors UIEntityReference). */
    triggerSearchSync?: boolean,
    /**
     * When set, re-stamp only the denormalized `entityRecordName` of the
     * already-selected reference (an inline rename of an embedded record).
     * Skips the id index. A no-op unless the reference for
     * this key points at the scoped record, and blank names are ignored (the
     * last non-blank name stands) — matching ReferenceSelect's `isBlank` rule.
     */
    renameTo?: string,
) => Promise<void>;

export const useSaveEntityReference = (): SaveEntityReference => {
    const { user } = useContext(UserContext);
    const { handleFirestoreWriteError } = useContext(MessageContext);
    const userId = user?.uid || '';
    const scope = useEntityRecordScope();
    const entityId = scope.entityId;
    const recordId = scope.recordId;
    const selectedEntityRecord = scope.entityRecord.data;

    return useCallback<SaveEntityReference>(
        async (value, entityGroupId, entityRecordData, triggerSearchSync, renameTo) => {
            try {
                if (!entityId) return;

                const live = snapshot(formStore.state);
                const insideForm = live.formId !== undefined;
                if (!insideForm) return;

                const formVersionData = live.formVersion.data as FormVersion | null;
                const formTypeData = live.formType.data as FormType | null;

                const existingRefs = formVersionData?.entityReferences ?? {};
                const entityOrGroupId = entityGroupId || entityId;
                const stamp = serverTimestamp() as TimeType;
                const editMeta = { lastModified: stamp, userId, agentType: AgentType.User };

                if (renameTo !== undefined) {
                    if (renameTo.trim() === '') return;
                    if (existingRefs[entityOrGroupId]?.entityRecordId !== recordId) return;
                    const nameUpdate: Record<string, unknown> = {
                        entityReferences: {
                            [entityOrGroupId]: { entityRecordName: renameTo, meta: editMeta },
                        },
                        meta: editMeta,
                    };
                    void formStore.getFormVersionPersist()?.setMerge(nameUpdate, { debounce: true });
                    return;
                }

                const updatedRefs = { ...existingRefs };
                const recordToUse = entityRecordData || selectedEntityRecord;

                if (value) {
                    updatedRefs[entityOrGroupId] = {
                        entityRecordId: value,
                        entityId,
                        entityRecordName: recordToUse?.name ?? '',
                        ...(entityGroupId ? { entityGroupId } : {}),
                        meta: {
                            created: existingRefs[entityOrGroupId]?.meta?.created || stamp,
                            ...editMeta,
                        },
                    };
                } else {
                    delete updatedRefs[entityOrGroupId];
                }

                const layoutMap = entityIdByReferenceKey(formTypeData?.defaultUILayout?.structure);
                const newEntityIds = entityReferenceIndex(updatedRefs, (key) => layoutMap[key]);

                const updateData: Record<string, unknown> = {
                    entityReferences: value ? updatedRefs : { [entityOrGroupId]: deleteField() },
                    allEntityIdsAndGroupIds: newEntityIds,
                    meta: editMeta,
                };

                const persist = formStore.getFormVersionPersist();
                if (triggerSearchSync && live.formId && live.versionId) {
                    await persist?.setMerge(updateData, { debounce: false });
                    void syncDocumentToSearch('formVersions', live.formId, live.versionId);
                } else {
                    void persist?.setMerge(updateData, { debounce: true });
                }
            } catch (error) {
                handleFirestoreWriteError(error, '[useSaveEntityReference]');
            }
        },
        [entityId, recordId, selectedEntityRecord, userId, handleFirestoreWriteError],
    );
};
